/**
 * ORA COMMUNITY PARTICIPANT SYSTEM
 * Three components: NovaThreadParticipant, NovaLiveRoomCoHost, NovaFeedInsert
 * All use the same detection logic and chapter config.
 */

// ── Spiral types (for NovaThreadParticipant) ─────────────────────
export const SPIRAL_TYPES = [
  'anxiety_amplification',
  'medical_decision',
  'grief_contagion',
  'misinformation',
  'long_wait',
];

// ── Spiral detection: keyword patterns per type ─────────────────
// In production this runs server-side on message content.

export const SPIRAL_TRIGGERS = {
  anxiety_amplification: [
    'cancelled', 'devastated', 'failed', "it didn't work",
    "never going to", "what's wrong with me", 'giving up',
  ],
  medical_decision: [
    'should I ask my doctor', 'my clinic said but I heard',
    'is it okay to skip', 'can I change my dose',
    'push back on', 'trigger at', 'should I trigger',
  ],
  grief_contagion: [
    'loss', 'miscarriage', 'chemical pregnancy', 'negative beta',
    "it didn't work", 'cycle failed', 'cancelled cycle',
  ],
  misinformation: [
    'I read online that', 'someone told me', 'I heard that',
    'protocol should be', 'you should tell your clinic to',
  ],
  long_wait: [
    'beta day', 'waiting for results', "they said they'd call",
    'checking my phone', "can't stop thinking",
  ],
};

// ── Chapter-specific Nova voice config ───────────────────────────
// Room topic → opening message. Chapter from room.chapters[0] or context.

export const NOVA_VOICE = {
  fertility: {
    communityTitle: 'Your community guide',
    roomTitle: 'room guide',
    openingByRoom: {
      'Stims support': "Stim days are a lot — physically and emotionally. I'm here with actual answers while you share. If something comes up that needs a clinic call, I'll help you know what to say.",
      'Trigger shot night': "Tonight's the night. Anxiety about trigger timing is completely normal. I'm here — ask me anything about what to expect in the next 36 hours.",
      'Retrieval recovery': "Retrieval day is huge. Rest, ask questions, share what helped. I'll add context if anything medical comes up.",
      'Transfer & TWW': "The two-week wait is one of the hardest stretches in this process. I'm here for the 2am spirals and the hopeful moments both.",
      'Beta day': "Waiting for beta results is genuinely one of the hardest hours in this whole journey. Whatever your result — I'm here. Type anything.",
      'First response tonight': "Trigger + 36h — you're in the thick of it. I'm here for whatever comes up.",
      'Low AMH / DOR support': "Low AMH or DOR diagnosis can feel like a door closing. It isn't. I'm here with real data and real people who've been where you are.",
      'TTC this cycle': "Peak timing questions are some of the most anxiety-producing in TTC. I'm here with actual data while you connect with each other.",
      'Two-week wait': "The TWW is its own kind of marathon. I'm here for the symptom-spotting spirals and the moments of hope. Ask me anything.",
    },
    circuitBreaker: "This thread is carrying a lot. You deserve support, not fixes. If it helps, here are grounded next steps women use when a cycle feels overwhelming:",
    redirectCTA: "I can help you find the right room or group right now →",
    spiralResponses: {
      anxiety_amplification: (ctx) => {
        const match = ctx?.match(/^(\d+)\s*(follicles?|eggs?)/i);
        const phrase = match ? `${match[1]} ${match[2]}` : null;
        return phrase
          ? `${phrase} is a real number, and what you're feeling is valid. I'll keep this practical: what this can mean, what to ask your clinic next, and where to find women in the same stage tonight.`
          : "Whatever your retrieval numbers look like, what you're feeling is valid. I'll keep this practical: what this can mean, what to ask your clinic next, and where to find women in the same stage tonight.";
      },
      medical_decision: () => "This one really depends on your specific protocol — your clinic set those parameters based on your scan results, which nobody here has seen. I can tell you what the general guidance is, and help you write exactly what to ask your doctor.",
      grief_contagion: () => "This thread is carrying a lot right now. If you've been reading for a while, you can step out and choose support that feels safer. Here are options women use in this moment:",
      misinformation: () => "Protocols vary a lot between clinics and patients — what worked for someone else's cycle may not apply to yours. Here's what the evidence says generally, and why your clinic's specific instruction matters more than community consensus.",
      long_wait: () => "Waiting is its own kind of hard. While you wait — here's what others in this room have found helpful, and I'm here if your result comes back and you need to talk through what it means.",
    },
  },
  ttc: {
    communityTitle: 'Your TTC guide',
    roomTitle: 'TTC guide',
    openingByRoom: {
      'TTC this cycle': "Peak timing questions are some of the most anxiety-producing in TTC. I'm here with actual data — not just reassurance — while you connect with each other.",
      'Two-week wait': "The TWW is its own kind of marathon. I'm here for the symptom-spotting spirals and the moments of hope. Ask me anything.",
    },
    circuitBreaker: "There's a lot of weight in this thread. It's okay to step away. Here's what's actually helped women through this moment:",
    redirectCTA: "I can help you prepare for your next appointment →",
    spiralResponses: {
      anxiety_amplification: (ctx) => `${ctx} — I hear you. Here's what the data actually shows about this situation, because you deserve real information right now, not just hope.`,
      medical_decision: () => "Cycle timing and treatment decisions really do depend on your specific history and your doctor's view of your data. I can give you the general picture — and help you frame exactly what to ask at your next appointment.",
      grief_contagion: () => "There's real pain in this thread. If you're in the middle of your own hard moment — you don't have to read everyone else's too. Here's what's helped others get through a negative cycle:",
      misinformation: () => "TTC advice varies a lot based on individual circumstances — what helped someone else conceive may not apply to your situation. Here's what's actually evidence-based:",
      long_wait: () => "The days before a test are some of the longest in TTC. I'm here for whatever the result is — and while you wait, here's what others in this room have done with the waiting:",
    },
  },
  pregnancy: {
    communityTitle: 'Your pregnancy guide',
    roomTitle: 'pregnancy guide',
    openingByRoom: {
      'Early pregnancy': "First trimester fear is so common — and so rarely talked about honestly. I'm here with real information while you support each other.",
    },
    circuitBreaker: "A lot of fear is moving through this thread. You don't have to absorb all of it. Here's what's actually helped with pregnancy anxiety:",
    redirectCTA: "I can help you write questions for your provider →",
    spiralResponses: {
      anxiety_amplification: (ctx) => `${ctx} is worth taking seriously — and worth getting accurate information about. Here's what the evidence shows, and what's worth mentioning to your OB.`,
      medical_decision: () => "Pregnancy decisions really do depend on your specific situation and your provider's judgment. I can give you the general context — and help you write the right questions for your next appointment.",
      grief_contagion: () => "This thread carries a lot of fear and grief. If you've been reading for a while and it's making the anxiety worse — it's okay to close it. Here's what's helped others manage pregnancy anxiety:",
      misinformation: () => "Pregnancy guidance varies a lot between providers and situations — general advice from others may not apply to your specific pregnancy. Here's what the evidence actually says:",
      long_wait: () => "Waiting for test results during pregnancy is one of the hardest kinds of waiting. I'm here for whatever the result is — and here's what others have found helpful in the meantime:",
    },
  },
  motherhood: {
    communityTitle: 'Your motherhood guide',
    roomTitle: 'motherhood guide',
    openingByRoom: {
      'Fourth trimester': "The fourth trimester is relentless and rarely talked about honestly. I'm here with real answers while you support each other through it.",
    },
    circuitBreaker: "This thread is heavy. It's okay to step back. Here's what's actually helped other mothers in this moment:",
    redirectCTA: "I can help you figure out what to tell your doctor →",
    spiralResponses: {
      anxiety_amplification: (ctx) => `${ctx} — what you're feeling is real. Here's what the research says about this, and when it's worth calling your provider.`,
      medical_decision: () => "Postpartum and infant health decisions really depend on your specific situation and your pediatrician's guidance. I can give you the general picture — and help you know what questions to ask.",
      grief_contagion: () => "There's a lot of pain in this thread — postpartum loss and struggle is real and it deserves space. If you're struggling yourself right now, here's where to find direct support:",
      misinformation: () => "Parenting advice varies enormously and a lot of it is outdated or not evidence-based. Here's what the current research actually says:",
      long_wait: () => "Waiting to hear from a doctor when something feels off with your baby or yourself is genuinely stressful. Here's what's worth knowing while you wait:",
    },
  },
  postpartum: {
    communityTitle: 'Your motherhood guide',
    roomTitle: 'motherhood guide',
    openingByRoom: {
      'Fourth trimester': "The fourth trimester is relentless and rarely talked about honestly. I'm here with real answers while you support each other through it.",
    },
    circuitBreaker: "This thread is heavy. It's okay to step back. Here's what's actually helped other mothers in this moment:",
    redirectCTA: "I can help you figure out what to tell your doctor →",
    spiralResponses: {
      anxiety_amplification: (ctx) => `${ctx} — what you're feeling is real. Here's what the research says about this, and when it's worth calling your provider.`,
      medical_decision: () => "Postpartum and infant health decisions really depend on your specific situation and your pediatrician's guidance. I can give you the general picture — and help you know what questions to ask.",
      grief_contagion: () => "There's a lot of pain in this thread — postpartum loss and struggle is real and it deserves space. If you're struggling yourself right now, here's where to find direct support:",
      misinformation: () => "Parenting advice varies enormously and a lot of it is outdated or not evidence-based. Here's what the current research actually says:",
      long_wait: () => "Waiting to hear from a doctor when something feels off with your baby or yourself is genuinely stressful. Here's what's worth knowing while you wait:",
    },
  },
  menopause: {
    communityTitle: 'Your menopause guide',
    roomTitle: 'menopause guide',
    openingByRoom: {
      'Peri & menopause': "Hot flashes are miserable and undertreated. I'm here with actual options — evidence-based, not just coping strategies — while you share what's working.",
    },
    circuitBreaker: "This thread is carrying a lot of weight. You don't have to keep reading. Here's what's actually helped women through this:",
    redirectCTA: "I can help you prepare for your doctor appointment →",
    spiralResponses: {
      anxiety_amplification: (ctx) => `${ctx} — that's a real experience and it deserves a real answer. Here's what the evidence shows and what's worth raising with your doctor.`,
      medical_decision: () => "HRT and menopause treatment decisions really depend on your specific history and risk profile. I can give you the general evidence picture — and help you write the questions that will get you heard at your appointment.",
      grief_contagion: () => "There's a lot of loss and grief in this thread — menopause brings real losses that aren't always acknowledged. If you're absorbing a lot right now, here's what's helped others:",
      misinformation: () => "Menopause information online is full of conflicting advice and outdated fear. Here's what the current evidence from The Menopause Society actually says:",
      long_wait: () => "Waiting for test results or appointment callbacks feels awful when you're already dealing with difficult symptoms. Here's what's worth knowing in the meantime:",
    },
  },
  cycle: {
    communityTitle: 'Your First Chapter guide',
    roomTitle: 'First Chapter guide',
    openingByRoom: {
      'Cycle tracking beginners': "Learning your cycle takes time — and there's no such thing as a dumb question. I'm here with clear answers while you connect with people your age.",
      'Cramps & bad period days': "Bad cramps are real, and you're allowed to take them seriously. I'm here with what helps — and when it's worth telling an adult or a nurse.",
      'Mood, PMS & big feelings': "Hormones can make feelings huge before your period — that doesn't make you dramatic. I'm here while you sort out what's normal and what might need support.",
      'Talk to parent / school nurse': "Those conversations feel impossible until you've had them once. I'm here with words you can borrow or adapt — no pressure.",
      'Body image & puberty': "Bodies change fast and social media lies. I'm here with grounded info while you talk with others who get it.",
    },
    circuitBreaker: "This thread has gotten heavy. It's okay to close it and come back later. Here's what's helped others:",
    redirectCTA: "I can help you figure out what to say →",
    spiralResponses: {
      anxiety_amplification: (ctx) => `${ctx} — that sounds really hard. Here's what's actually normal, and what's worth talking to a doctor about.`,
      medical_decision: () => "That's a question for a doctor or a trusted adult — not something to decide based on what others online have experienced. I can help you figure out how to have that conversation.",
      grief_contagion: () => "There's a lot of pain in this thread. You don't have to read all of it. Here's what's helped others get through hard moments:",
      misinformation: () => "There's a lot of confusing information out there about this. Here's what's actually true based on the evidence:",
      long_wait: () => "Waiting to find out something health-related when you're worried is really stressful. Here's what's worth knowing while you wait:",
    },
  },
  'first-chapter': {
    communityTitle: 'Your First Chapter guide',
    roomTitle: 'First Chapter guide',
    openingByRoom: {
      'Cycle tracking beginners': "Learning your cycle takes time — and there's no such thing as a dumb question. I'm here with clear answers while you connect with people your age.",
      'Cramps & bad period days': "Bad cramps are real, and you're allowed to take them seriously. I'm here with what helps — and when it's worth telling an adult or a nurse.",
      'Mood, PMS & big feelings': "Hormones can make feelings huge before your period — that doesn't make you dramatic. I'm here while you sort out what's normal and what might need support.",
      'Talk to parent / school nurse': "Those conversations feel impossible until you've had them once. I'm here with words you can borrow or adapt — no pressure.",
      'Body image & puberty': "Bodies change fast and social media lies. I'm here with grounded info while you talk with others who get it.",
    },
    circuitBreaker: "This thread has gotten heavy. It's okay to close it and come back later. Here's what's helped others:",
    redirectCTA: "I can help you figure out what to say →",
    spiralResponses: {
      anxiety_amplification: (ctx) => `${ctx} — that sounds really hard. Here's what's actually normal, and what's worth talking to a doctor about.`,
      medical_decision: () => "That's a question for a doctor or a trusted adult — not something to decide based on what others online have experienced. I can help you figure out how to have that conversation.",
      grief_contagion: () => "There's a lot of pain in this thread. You don't have to read all of it. Here's what's helped others get through hard moments:",
      misinformation: () => "There's a lot of confusing information out there about this. Here's what's actually true based on the evidence:",
      long_wait: () => "Waiting to find out something health-related when you're worried is really stressful. Here's what's worth knowing while you wait:",
    },
  },
};

/** Circuit-breaker suggestions — chapter-specific "what's actually helped" list */
const CIRCUIT_BREAKER_SUGGESTIONS = {
  fertility: [
    { emoji: '🫶', text: "Join 'Starting again after a failed cycle' so you're not carrying this alone." },
    { emoji: '💬', text: "Open 'Retrieval recovery' or 'Stims support' and post one honest line about where you are." },
    { emoji: '📞', text: 'Send one message to your partner or closest person: "I need support tonight."' },
    { emoji: '📝', text: 'Write 2 clinic questions for your follow-up so your next step feels clearer.' },
  ],
  ttc: [
    { emoji: '🚶', text: 'A short walk — not for fitness, just to move out of your head' },
    { emoji: '📵', text: "Put the phone down for 30 minutes. The results won't change." },
    { emoji: '☕', text: 'Make something warm. It genuinely helps the nervous system.' },
    { emoji: '💬', text: "Tell one person how you're actually doing today" },
  ],
  pregnancy: [
    { emoji: '🫁', text: 'Four slow breaths. Anxiety physically cannot sustain itself through this.' },
    { emoji: '📋', text: 'Write your questions down for your next appointment instead of searching now' },
    { emoji: '🎵', text: "Something you loved before you were pregnant — music, a show, anything" },
    { emoji: '🤰', text: 'Your body is doing something extraordinary. Give it some credit.' },
  ],
  motherhood: [
    { emoji: '😴', text: 'Sleep when you can. Everything else can wait.' },
    { emoji: '🍽', text: "Eat something. You probably haven't." },
    { emoji: '🤝', text: 'Ask for help with one specific thing today' },
    { emoji: '📞', text: "If you're struggling, call your OB. That's what they're there for." },
  ],
  postpartum: [
    { emoji: '😴', text: 'Sleep when you can. Everything else can wait.' },
    { emoji: '🍽', text: "Eat something. You probably haven't." },
    { emoji: '🤝', text: 'Ask for help with one specific thing today' },
    { emoji: '📞', text: "If you're struggling, call your OB. That's what they're there for." },
  ],
  menopause: [
    { emoji: '🌊', text: 'Cool water on your wrists and neck — it genuinely helps hot flashes' },
    { emoji: '📓', text: "Log what you're feeling — it's data for your doctor, not just a diary" },
    { emoji: '🚶', text: 'A short walk can reset cortisol better than anything else' },
    { emoji: '💬', text: "You deserve to be taken seriously. Nova can help you prepare that argument." },
  ],
  cycle: [
    { emoji: '🌙', text: "It's okay not to know everything about your body yet. Nobody did at first." },
    { emoji: '📓', text: "Write down how you're feeling — not for anyone else, just for you" },
    { emoji: '☕', text: 'Something warm. Something quiet. You\'re allowed a break.' },
    { emoji: '💬', text: "Is there one adult in your life you trust? They'd want to know you're struggling." },
  ],
  'first-chapter': [
    { emoji: '🌙', text: "It's okay not to know everything about your body yet. Nobody did at first." },
    { emoji: '📓', text: "Write down how you're feeling — not for anyone else, just for you" },
    { emoji: '☕', text: 'Something warm. Something quiet. You\'re allowed a break.' },
    { emoji: '💬', text: "Is there one adult in your life you trust? They'd want to know you're struggling." },
  ],
};

/** Get circuit-breaker suggestions for NovaThreadParticipant and NovaFeedInsert */
export function getCircuitBreakerSuggestions(chapter) {
  const key = chapter === 'trying-to-conceive' ? 'ttc' : chapter;
  return CIRCUIT_BREAKER_SUGGESTIONS[key] ?? CIRCUIT_BREAKER_SUGGESTIONS.fertility;
}

/** Evidence summary for anxiety_amplification "WHAT THE EVIDENCE SHOWS" block */
const DEFAULT_EVIDENCE = 'Individual experiences vary significantly. What\'s true for one person\'s situation may not apply to yours — your provider has your specific data and history, which matters more than general patterns.';

export function getEvidenceSummary(chapter, spiralType) {
  if (spiralType !== 'anxiety_amplification') return '';
  if (chapter === 'fertility') {
    return "Retrieval numbers, embryo development, and outcomes can vary a lot between cycles for the same person. A hard result is still hard, but it does not define your entire prognosis. Your clinic has your full protocol and history — that context matters more than any single thread or single metric.";
  }
  if (chapter === 'ttc' || chapter === 'trying-to-conceive') {
    return "Conception timelines vary enormously — studies show that even for couples with no fertility issues, 12 months is considered normal. Month 4 is not late. The anxiety you're feeling is real; the conclusion you're drawing may not be.";
  }
  if (chapter === 'pregnancy') {
    return "Most pregnancy anxieties are based on worst-case scenarios that represent a small minority of outcomes. Your OB has your specific data — community threads reflect the loudest experiences, not the most common ones.";
  }
  if (chapter === 'motherhood' || chapter === 'postpartum') {
    return "Postpartum recovery timelines vary widely and are rarely discussed honestly. What you're experiencing is more common than the silence around it suggests. If you're concerned, call your OB — that's what the 6-week visit is designed for.";
  }
  if (chapter === 'menopause') {
    return "Perimenopause and menopause symptoms are often undertreated because they're undertested. What you're experiencing has real physiological causes — and real evidence-based treatments. The Menopause Society guidelines are worth knowing.";
  }
  return DEFAULT_EVIDENCE;
}

/** Resolve chapter from room (LIVE_CHAT_ROOMS have chapters: ['ivf'], etc.) */
export function getChapterForRoom(room) {
  if (!room?.chapters?.length) return 'fertility';
  const ch = room.chapters[0];
  const map = { ivf: 'fertility', baby: 'motherhood' };
  return map[ch] || ch;
}

/** Get Nova's opening message for a Live Room */
export function getNovaRoomOpening(room) {
  const chapter = getChapterForRoom(room);
  const config = NOVA_VOICE[chapter] || NOVA_VOICE.fertility;
  const opening = config.openingByRoom?.[room?.topic];
  if (opening) return { primary: opening, seeLink: 'See →', footer: "I'm here. Type anything.", cta: config.redirectCTA };
  return {
    primary: "I'm here as your room guide. Ask me anything, or lean on the women in this room.",
    seeLink: 'Resources →',
    footer: "Nothing's TMI. Type anything.",
    cta: config.redirectCTA,
  };
}

/** Get Nova's room title label (e.g. "room guide", "TTC guide") */
export function getNovaRoomTitle(room) {
  const chapter = getChapterForRoom(room);
  const config = NOVA_VOICE[chapter] || NOVA_VOICE.fertility;
  return config.roomTitle || 'room guide';
}

/** High-risk rooms (Beta day, results waiting, grief) get prominent Nova presence */
const HIGH_RISK_ROOMS = ['Beta day', 'First response tonight', 'After a negative'];

export function getSpiralRiskForRoom(room) {
  if (!room?.topic) return 'low';
  return HIGH_RISK_ROOMS.includes(room.topic) ? 'high' : 'low';
}

/** Detect spiral type from post text (simplified — production uses NLP) */
export function detectSpiral(post) {
  const text = (post?.text || '').toLowerCase();
  if (!text) return null;
  for (const [type, keywords] of Object.entries(SPIRAL_TRIGGERS)) {
    const hit = keywords.some((k) => text.includes(k.toLowerCase()));
    if (hit) return { type, confidence: 'high' };
  }
  return null;
}

/** Pattern notice headline — "what's happening in the chapter today" */
const CHAPTER_PATTERN_HEADLINES = {
  fertility: "7 women in stims support right now — scan results coming in",
  ttc: "12 women in the TWW this week — fertile window closed yesterday",
  pregnancy: "Week 20 anatomy scans happening this week — 14 in your group",
  motherhood: "Fourth trimester room is active — 22 women in weeks 4–8 right now",
  postpartum: "Fourth trimester room is active — 22 women in weeks 4–8 right now",
  menopause: "HRT questions room active — 11 women comparing experiences",
  cycle: "9 women learning their cycle this week — all at different stages",
  'first-chapter': "9 women learning their cycle this week — all at different stages",
};

/** Pattern notice body — Nova's proactive message */
const CHAPTER_PATTERN_BODIES = {
  fertility: "Based on what's being shared today, the most common question is about monitoring results and what the numbers actually mean. I've put together what the evidence shows.",
  ttc: "The most common thread this week is OPK interpretation and fertile window timing. Here's what's actually evidence-based versus what's anxiety talking.",
  pregnancy: "Anatomy scan anxiety is the dominant topic this week. Here's exactly what they look at, and what questions are worth asking.",
  motherhood: "Sleep, feeding, and 'is this normal' are the top three this week. Here's what the research actually says about each.",
  postpartum: "Sleep, feeding, and 'is this normal' are the top three this week. Here's what the research actually says about each.",
  menopause: "Hot flash timing and HRT questions are active today. Here's what the current evidence shows — not the outdated fear.",
  cycle: "Cycle irregularity and cramping are the top topics this week. Here's what's normal and what's worth mentioning to a doctor.",
  'first-chapter': "Cycle irregularity and cramping are the top topics this week. Here's what's normal and what's worth mentioning to a doctor.",
};

export function getChapterPatternHeadline(chapter) {
  return CHAPTER_PATTERN_HEADLINES[chapter] ?? "Women like you are active right now";
}

export function getChapterPatternBody(chapter) {
  return CHAPTER_PATTERN_BODIES[chapter] ?? "I've been watching what's coming up in your chapter today.";
}
